import * as React from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import { RouteTemplates, makeRoutes } from './routes';
import { CopyrightStatement, PrivacyStatement } from '../legal/Statement';
import YmcaLoginPage from '../auth/sso/YmcaLoginPage';
import YmcaSsoPage from '../auth/sso/YmcaSsoPage';
import SamlAssertionPage from '../auth/sso/SamlAssertionPage';
import SsoErrorPage from '../auth/sso/SsoErrorPage';
import LoginPage from '../auth/login/LoginPage';
import RaterApprovalRedirect from '../auth/login/RaterApprovalRedirect';
import ForgotPasswordPage from '../auth/forgotPassword/ForgotPasswordPage';
import { ActionType } from '../core/redux/actionType';
import {
  sendRaterApprovalPasswordResetEmailAction,
} from '../auth/forgotPassword/api/sendRaterApprovalPasswordResetEmailAction';
import ProtectedRoute from '../auth/ProtectedRoute';
import AccountSetupPage from '../auth/accountSetup/AccountSetupPage';
import RaterApprovalRouter from '../raterApproval/RaterApprovalRouter';
import ReportRecipientRedirect from '../reportRecipient/ReportRecipientRedirect';
import {
  sendReportRecipientPasswordResetEmailAction,
} from '../auth/forgotPassword/api/sendReportRecipientPasswordResetEmailAction';
import ReportRecipientRouter from '../reportRecipient/ReportRecipientRouter';
import ProjectModal from '../project/ProjectModal';
import { sendPortalPasswordResetEmailAction } from '../auth/forgotPassword/api/sendPortalPasswordResetEmailAction';
import ProjectRouter from '../project/ProjectRouter';
import CollaboratorRoute from '../project/devPlan/collaboration/CollaboratorRoute';
import CollaboratorDevPlanRouter from '../project/devPlan/collaboration/CollaboratorDevPlanRouter';
import FileData from '../fileData/FileData';


/**
 * AppRouter: a component that
 */
const AppRouter: React.FC = () => {
  const routes = makeRoutes();

  return (
    <Switch>
      <Route path={RouteTemplates.Copyright} component={CopyrightStatement} />
      <Route path={RouteTemplates.Privacy} component={PrivacyStatement} />

      <Route path={RouteTemplates.YmcaLogin} component={YmcaLoginPage} />
      <Route path={RouteTemplates.YmcaSso} component={YmcaSsoPage} />
      <Route path={RouteTemplates.SamlAssertion} component={SamlAssertionPage} />
      <Route path={RouteTemplates.SsoError} component={SsoErrorPage} />

      <Route
        path={RouteTemplates.RaterApprovalLogin}
        render={
          () => (
            <LoginPage
              forgotPasswordUrl={routes.RaterApprovalForgotPassword}
              accountSetupUrl={routes.RaterApprovalAccountSetup}
              postLogin={<RaterApprovalRedirect />}
            />
          )
        }
      />
      <Route
        path={RouteTemplates.RaterApprovalForgotPassword}
        render={
          () => (
            <ForgotPasswordPage
              loginUrl={routes.RaterApprovalLogin}
              requestActionType={ActionType.SendRaterApprovalPasswordResetEmailRequest}
              requestPasswordResetEmail={sendRaterApprovalPasswordResetEmailAction}
            />
          )
        }
      />
      <ProtectedRoute
        path={RouteTemplates.RaterApprovalAccountSetup}
        component={() => <AccountSetupPage postSetup={<RaterApprovalRedirect />} />}
      />

      <ProtectedRoute path={RouteTemplates.RaterApprovalDashboard} component={RaterApprovalRouter} />
      <Redirect from={RouteTemplates.RaterApproval} to={routes.RaterApprovalLogin} />

      <Route
        path={RouteTemplates.ReportRecipientLogin}
        render={
          () => (
            <LoginPage
              forgotPasswordUrl={routes.ReportRecipientForgotPassword}
              accountSetupUrl={routes.ReportRecipientAccountSetup}
              postLogin={<ReportRecipientRedirect />}
            />
          )
        }
      />
      <Route
        path={RouteTemplates.ReportRecipientForgotPassword}
        render={
          () => (
            <ForgotPasswordPage
              loginUrl={routes.ReportRecipientLogin}
              requestActionType={ActionType.SendReportRecipientPasswordResetEmailRequest}
              requestPasswordResetEmail={sendReportRecipientPasswordResetEmailAction}
            />
          )
        }
      />
      <ProtectedRoute
        path={RouteTemplates.ReportRecipientAccountSetup}
        component={() => <AccountSetupPage postSetup={<ReportRecipientRedirect />} />}
      />

      <ProtectedRoute path={RouteTemplates.ReportRecipientDashboard} component={ReportRecipientRouter} />
      <ProtectedRoute path={RouteTemplates.ReportRecipientAggregateDashboard} component={ReportRecipientRouter} />
      <Redirect from={RouteTemplates.ReportRecipient} to={routes.ReportRecipientLogin} />

      <Route
        path={routes.Login}
        render={
          () => (
            <LoginPage
              forgotPasswordUrl={routes.ForgotPassword}
              accountSetupUrl={routes.AccountSetup}
              postLogin={
                <ProjectModal />
              }
            />
          )
        }
      />
      <Route
        path={RouteTemplates.ForgotPassword}
        render={
          () => (
            <ForgotPasswordPage
              loginUrl={routes.Login}
              requestActionType={ActionType.SendPortalPasswordResetEmailRequest}
              requestPasswordResetEmail={sendPortalPasswordResetEmailAction}
            />
          )
        }
      />
      <ProtectedRoute
        path={RouteTemplates.AccountSetup}
        component={() => <AccountSetupPage postSetup={<ProjectModal />} />}
      />
      <CollaboratorRoute path={RouteTemplates.Collaborator} component={CollaboratorDevPlanRouter} />
      <ProtectedRoute path={RouteTemplates.Project} component={ProjectRouter} />

      <ProtectedRoute
        path={RouteTemplates.FileDataDevelopmentPlan}
        component={() => <FileData folder='DevelopmentPlans' />}
      />
      <ProtectedRoute
        path={RouteTemplates.FileDataDevelopmentTip}
        component={() => <FileData folder='DevelopmentTips' />}
      />
      <ProtectedRoute
        path={RouteTemplates.FileDataDocument}
        component={() => <FileData folder='Documents' />}
      />
      <ProtectedRoute
        path={RouteTemplates.FileDataResource}
        component={() => <FileData folder='Resources' />}
      />

      {/* All unmatched routes go to login */}
      <Redirect from='*' to={routes.Login} />
    </Switch>
  );
};

export default AppRouter;
